import { forwardRef } from "@angular/core";
import { DialogRef, WidgetRef } from '../widgets/framework/types';
import {
  encodeUrl,
  FormWidgetType,
  isValidWebURL,
  stringStartsWith,
  deHyphenate,
  initCaps,
  prettifyLabel,
  runtimeEventManager
} from '@wm/core';
import { assignWith, cloneDeep, forEach, includes, isArray, isObject, isString, join, split } from "lodash-es";

const DATASET_WIDGETS = new Set([FormWidgetType.SELECT, FormWidgetType.CHECKBOXSET, FormWidgetType.RADIOSET,
FormWidgetType.SWITCH, FormWidgetType.AUTOCOMPLETE, FormWidgetType.CHIPS, FormWidgetType.TYPEAHEAD, FormWidgetType.RATING]);

export const provideAs = (reference: any, key: any, multi?: boolean) => {
  return {
    provide: WidgetRef,
    useExisting: forwardRef(() => reference),
    multi: multi
  };
}

export const provideAsWidgetRef = (reference: any) => {
  return provideAs(reference, WidgetRef);
}

export const provideAsDialogRef = (reference: any) => {
  return provideAs(reference, DialogRef);
}

export function provideAsParentWidgetRef(reference: any) {
  // return {provide: Parent, useExisting: forwardRef(() => reference)}
}

// get project-id query param from the URL and return. Without using Angular's Location service
export function getProjectIdFromUrl() {
  const url = window.location.href;
  const projectId = url.split('?')[1].split('&')[0].split('=')[1];
  return projectId;
}

function getProjectResourcePath() {
  const projectId = getProjectIdFromUrl();
  //TODO: Inject this 'studio' context from the app
  return '/studio/services/projects/' + projectId + '/resources/content/web/';
}

// This method returns the url to the backgroundImage
export const getBackGroundImageUrl = (urlString: string) => {
  if (urlString === '' || urlString === 'none') {
    return urlString;
  }
  return 'url(' + getImageUrl(urlString) + ')';
};

/*This function returns the url to the image after checking the validity of url*/
export const getImageUrl = (urlString: string, shouldEncode?: string, defaultUrl?: string) => {
  /*In studio mode before setting picturesource, check if the studioController is loaded and new picturesource is in 'styles/images/' path or not.
   * When page is refreshed, loader.gif will be loaded first and it will be in 'style/images/'.
   * Prepend 'services/projects/' + $rootScope.project.id + '/web/resources/images/imagelists/'  if the image url is just image name in the project root,
   * and if the url pointing to resources/images/ then 'services/projects/' + $rootScope.project.id + '/web/'*/
  if (isValidWebURL(urlString)) {
    return urlString;
  }

  // If no value is provided for picturesource assign pictureplaceholder or default-image
  if (!urlString) {
    urlString = defaultUrl || 'resources/images/imagelists/default-image.png';
  }

  // in studio, resource should be loaded from the service URL
  urlString = getProjectResourcePath() + urlString;

  // may not be required in studio
  urlString = shouldEncode ? encodeUrl(urlString) : urlString;

  // if the resource to be loaded is inside a prefab
  if (stringStartsWith(urlString, 'services/prefabs')) {
    return urlString;
  }
  return urlString;
};

export const getWatchIdentifier = (...args: any[]) => args.join('_');

// Returns array of classes that are evaluated true for given object or array
const getClassesArray = (classVal: any) => {
  let classes: any[] = [];

  if (isArray(classVal)) {
    classVal.forEach(v => {
      classes = classes.concat(getClassesArray(v));
    });
    return classes;
  }
  if (isObject(classVal)) {
    forEach(classVal, (val, key) => {
      if (val) {
        classes = classes.concat(key.split(' '));
      }
    });
    return classes;
  }
  return classes;
};

export const getConditionalClasses = (nv: { toAdd: any; toRemove: any; }, ov?: any) => {
  let toAdd;
  let toRemove;
  // if the conditional class property has already toAdd and toRemove arrays then take that otherwise build those arrays
  const classToAdd = nv.toAdd || nv;
  const classToRemove = nv.toRemove || ov;
  if (isObject(nv)) {
    toAdd = isArray(classToAdd) ? classToAdd : getClassesArray(classToAdd || []);
    toRemove = classToRemove ? (isArray(classToRemove) ? classToRemove : getClassesArray(classToRemove)) : [];
  } else {
    toAdd = classToAdd ? [classToAdd] : [];
    toRemove = classToRemove ? [classToRemove] : [];
  }
  return { toAdd, toRemove };
};

export const updateWidgetProps = (widgetProps: any, show: boolean, propsToShow: string[], propsToHide?: string[]) => {
  let wProps: any = widgetProps;
  forEach(propsToShow, (value) => {
    if (wProps[value]) {
      wProps[value].show = show;
    }
  });
  forEach(propsToHide, function (value) {
    if (wProps[value]) {
      wProps[value].show = !show;
    }
  });
  // to refresh the properties panel.
  runtimeEventManager.emit('wms:refresh-properties-panel');
}

/**
 * Returns the orderBy Expression based on the 'sort 'option in pageable object
 * returned by backend
 * @param pageableObj
 * @returns {string}
 */
export const getOrderByExpr = (pageableObj: any) => {
  pageableObj = pageableObj || [];
  const expressions: any = [],
    KEY_VAL_SEPARATOR = ' ',
    FIELD_SEPARATOR = ',';
  forEach(pageableObj, obj => {
    if (obj.direction) {
      expressions.push(obj.property + KEY_VAL_SEPARATOR + obj.direction.toLowerCase());
    }
  });

  return join(expressions, FIELD_SEPARATOR);
};

export const NAVIGATION_TYPE = {
  ADVANCED: 'Advanced',
  BASIC: 'Basic',
  CLASSIC: 'Classic',
  INLINE: 'Inline',
  NONE: 'None',
  ONDEMAND: 'On-Demand',
  PAGER: 'Pager',
  SCROLL: 'Scroll'
};

export const isDataSetWidget = (widget: any) => {
  return DATASET_WIDGETS.has(widget);
};

export const prepareFieldDefs = (data: any, options?: any) => {
  let dataObject;
  const columnDef = {
    'objects': [],
    'terminals': []
  };
  /*if no data provided, initialize default column definitions*/
  if (!data) {
    data = [];
  }
  if (!options) {
    options = {};
  }
  options.setBindingField = true;
  options.columnCount = 0;
  dataObject = getMetaDataFromData(data);
  /*first of the many data objects from grid data*/
  pushFieldDef(dataObject, columnDef, '', options);
  if (!options || (options && !options.filter)) {
    return columnDef.terminals;
  }
  switch (options.filter) {
    case 'all':
      return columnDef;
    case 'objects':
      return columnDef.objects;
    case 'terminals':
      return columnDef.terminals;
  }
  return columnDef;
};

const getMetaDataFromData = (data: any) => {
  let dataObject: any;
  if (isArray(data)) {
    if (isObject(data[0])) {
      dataObject = cloneDeep(data[0]);
      /*Loop over the object to find out any null values. If any null values are present in the first row, check and assign the values from other row.
       * As column generation is dependent on data, for related fields if first row value is null, columns are not generated.
       * To prevent this, check the data in other rows and generate the columns. New keys from others rows are also added*/
      forEach(data, (row, index) => {
        if ((index + 1) >= 10) { // Limit the data search to first 10 records
          return false;
        }
        assignWith(dataObject, row, (objValue, srcValue) => {
          return (objValue === null || objValue === undefined) ? srcValue : objValue;
        });
        return;
      });
    } else {
      dataObject = data[0];
    }
  } else {
    dataObject = data;
  }
  return dataObject;
};

/*helper function for prepareFieldDefs*/
const pushFieldDef = (dataObject: any, columnDefObj: any, namePrefix: any, options: any) => {
  /*loop over the fields in the dataObject to process them*/
  let modifiedTitle,
    relatedTable: any,
    relatedField: any,
    relatedInfo,
    fieldName,
    isRelated: any;
  if (!options) {
    options = {};
  }
  forEach(dataObject, (value, title) => {
    if (includes(title, '.')) {
      relatedInfo = split(title, '.');
      relatedTable = relatedInfo[0];
      relatedField = relatedInfo[1];
      isRelated = true;
    }
    if (options.noModifyTitle) {
      modifiedTitle = title;
    } else {
      if (isString(title)) {
        modifiedTitle = prettifyLabel(title);
        modifiedTitle = deHyphenate(modifiedTitle);
        modifiedTitle = namePrefix ? initCaps(namePrefix) + ' ' + modifiedTitle : modifiedTitle;
      } else {
        modifiedTitle = title;
      }
    }
    title = namePrefix ? namePrefix + '.' + title : title;
    if (isRelated) {
      // For related columns, shorten the title to last two words
      fieldName = split(modifiedTitle, ' ');
      fieldName = fieldName.length > 1 ? fieldName[fieldName.length - 2] + ' ' + fieldName[fieldName.length - 1] : fieldName[0];
    } else {
      fieldName = modifiedTitle;
    }
    const defObj = options.setBindingField ? { 'displayName': fieldName, 'field': title, 'relatedTable': relatedTable, 'relatedField': relatedField || modifiedTitle }
      : { 'displayName': fieldName, 'relatedTable': relatedTable, 'relatedField': relatedField || modifiedTitle };
    /*if field is a leaf node, push it in the columnDefs*/
    if (!isObject(value) || (isArray(value) && !value[0])) {
      /*if the column counter has reached upperBound return*/
      if (options.upperBound && options.columnCount === options.upperBound) {
        return;
      }
      columnDefObj.terminals.push(defObj);
      /*increment the column counter*/
      options.columnCount += 1;
    } else {
      /*else field is an object, process it recursively*/
      /* if parent node to be included, include it */
      if (options.columnCount !== options.upperBound) {
        columnDefObj.objects.push(defObj);
      }

      /* if field is an array node, process its first child */
      if (isArray(value) && value[0]) {
        pushFieldDef(value[0], columnDefObj, title + '[0]', options);
      } else {
        pushFieldDef(value, columnDefObj, title, options);
      }
    }
  });
};

/**
 * To extract the Variable Name from a Widget's binddatasource attribute
 */
export const extractDataSourceName = (bindDataSource: any) => {
  if (!bindDataSource) {
      return;
  }
  let dataSourceName;
  const parts = bindDataSource.split('.');
  if (parts.length > 1 && parts[0] === 'Variables') {
      dataSourceName = parts[1];
  }
  return dataSourceName;
}

export const getNavClass = (suffix: string) : string => {
  const APP_NAV_CLASS_PREFIX = 'app-nav-';
  return APP_NAV_CLASS_PREFIX + suffix;
}

export const getSheetPositionClass = (suffix: string): string => {
  const SHEET_POSITION_CLASS_PREFIX = 'sheet-position-';
  return SHEET_POSITION_CLASS_PREFIX + suffix;
}
